import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { MapSkeleton } from '../Common/PageSkeleton';
import { GoogleMap, useJsApiLoader, MarkerF, PolylineF } from '@react-google-maps/api';
import logo from '../../assets/waseeny-logo.png';
import api from '../../config/api';
import { getSessionToken } from '../../utils/authSession';
import { io } from 'socket.io-client';
import FormBanner from '../Common/FormBanner';
import './LiveTracking.css';

const mapContainerStyle = {
  width: '100%',
  height: '100%',
};

const center = { lat: 51.4994, lng: 7.0833 }; // Default Germany coordinates

const libraries = ['places'];

const LiveTracking = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries
  });

  const [status, setStatus] = useState('preparing');
  const [riderPos, setRiderPos] = useState(null);
  const [restaurantPos, setRestaurantPos] = useState(null);
  const [userPos, setUserPos] = useState(null);
  const [riderInfo, setRiderInfo] = useState(null);
  const [eta, setEta] = useState(25);
  const [deliveryOtp, setDeliveryOtp] = useState('');
  const [formBanner, setFormBanner] = useState({ type: '', text: '' });

  const simulationInterval = useRef(null);

  useEffect(() => {
    const fetchOrder = async () => {
      try {
        let res;
        try {
          res = await api.get(`/orders/${id}/details`);
        } catch (err) {
          res = await api.get(`/orders/${id}/customer`);
        }
        const orderData = res.data?.order || res.data?.data || res.data;
        if (!orderData) return;
        setStatus(orderData.status || 'preparing');
        setRiderInfo(orderData.rider || null);
        setDeliveryOtp(orderData.otps?.delivery?.otp || orderData.deliveryOtp || orderData.otp || '');

        // Extract positions
        if (orderData.restaurant?.location?.coordinates) {
          setRestaurantPos({
            lat: orderData.restaurant.location.coordinates[1],
            lng: orderData.restaurant.location.coordinates[0]
          });
          // Initial rider position at restaurant
          setRiderPos({
            lat: orderData.restaurant.location.coordinates[1],
            lng: orderData.restaurant.location.coordinates[0]
          });
        }

        if (orderData.deliveryAddress?.location?.coordinates) {
          setUserPos({
            lat: orderData.deliveryAddress.location.coordinates[1],
            lng: orderData.deliveryAddress.location.coordinates[0]
          });
        }
      } catch (err) {
        console.error("Error fetching order:", err);
      }
    };
    fetchOrder();
  }, [id]);

  // Real-time Socket Integration
  useEffect(() => {
    const token = getSessionToken();
    if (!token) return;

    const apiBase = process.env.REACT_APP_API_URL;
    const socketUrl = apiBase.replace(/\/api\/?$/, '');

    const socket = io(socketUrl, { auth: { token } });

    socket.on('connected', () => {});

    // Listen for Order Status changes
    socket.on('order:status', (data) => {
      if (data.orderId === id) {
        setStatus(data.status);
      }
    });

    // Listen for Real Rider Location Updates
    socket.on('rider:location_updated', (data) => {
      if (data.orderId === id && data.riderLocation) {
        setRiderPos({
          lat: data.riderLocation.lat,
          lng: data.riderLocation.long
        });
        if (data.eta) setEta(data.eta);
      }
    });

    return () => {
      socket.disconnect();
    };
  }, [id]);

  if (!isLoaded) return <MapSkeleton />;


  const getStatusStep = () => {
    const s = (status || '').toLowerCase();
    if (['placed', 'accepted', 'preparing'].includes(s)) return 1;
    if (['ready', 'picked_up', 'out_for_delivery'].includes(s)) return 2;
    if (s === 'delivered') return 3;
    return 1;
  };

  return (
    <div className="live-tracking-container">
      {/* Header */}
      <header className="tracking-header-premium">
        <div className="header-left">
          <button className="back-btn" onClick={() => navigate(-1)}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M19 12H5M12 19l-7-7 7-7" />
            </svg>
          </button>
          <div className="order-info">
            <h1>Tracking Order</h1>
            <p>#{id?.slice(-8).toUpperCase()}</p>
          </div>
        </div>
        <img src={logo} alt="Waseeny" className="tracking-logo-small" />
      </header>

      {/* Map Section */}
      <div className="map-wrapper">
        <GoogleMap
          mapContainerStyle={mapContainerStyle}
          center={riderPos || restaurantPos || center}
          zoom={15}
          options={{
            disableDefaultUI: true,
            styles: [
              { featureType: "all", elementType: "geometry.fill", stylers: [{ weight: "2.00" }] },
              { featureType: "all", elementType: "geometry.stroke", stylers: [{ color: "#9c9c9c" }] },
              { featureType: "all", elementType: "labels.text.fill", stylers: [{ color: "#746153" }] },
              { featureType: "all", elementType: "labels.text.stroke", stylers: [{ color: "#f5f1e6" }] },
              { featureType: "landscape", elementType: "all", stylers: [{ color: "#f2f2f2" }] },
              { featureType: "road", elementType: "all", stylers: [{ saturation: -100 }, { lightness: 45 }] },
              { featureType: "water", elementType: "all", stylers: [{ color: "#d2d2d2" }, { visibility: "on" }] }
            ]
          }}
        >
          {restaurantPos && (
            <MarkerF
              position={restaurantPos}
              icon={{
                url: 'https://cdn-icons-png.flaticon.com/512/3448/3448609.png',
                scaledSize: new window.google.maps.Size(40, 40)
              }}
            />
          )}
          {userPos && (
            <MarkerF
              position={userPos}
              icon={{
                url: 'https://cdn-icons-png.flaticon.com/512/1216/1216733.png',
                scaledSize: new window.google.maps.Size(40, 40)
              }}
            />
          )}
          {riderPos && (
            <MarkerF
              position={riderPos}
              icon={{
                url: 'https://cdn-icons-png.flaticon.com/512/2972/2972185.png',
                scaledSize: new window.google.maps.Size(50, 50)
              }}
            />
          )}
          {riderPos && userPos && (
            <PolylineF
              path={[riderPos, userPos]}
              options={{
                strokeColor: "#C5192D",
                strokeOpacity: 0.6,
                strokeWeight: 4,
                icons: [{ icon: { path: 'M 0,-1 0,1', strokeOpacity: 1, scale: 4 }, offset: '0', repeat: '20px' }]
              }}
            />
          )}
        </GoogleMap>

        {/* Floating Status Card */}
        <div className="floating-tracking-card">
          <div className="eta-badge">
            <span className="eta-val">{Math.round(eta)}</span>
            <span className="eta-unit">MINS</span>
          </div>
          <div className="tracking-status-text">
            <h2>{status === 'delivered' ? 'Delivered!' : status === 'out_for_delivery' ? 'On the way' : 'Preparing food'}</h2>
            <p>{status === 'delivered' ? 'Enjoy your meal' : 'Your rider is moving towards you'}</p>
          </div>
        </div>
      </div>

      {/* Bottom Sheet */}
      <div className="tracking-bottom-sheet">
        <FormBanner
          message={formBanner.text}
          type={formBanner.type || 'error'}
          onDismiss={() => setFormBanner({ type: '', text: '' })}
          className="tracking-form-banner"
        />
        <div className="sheet-handle"></div>

        {/* Progress Tracker */}
        <div className="premium-progress-bar">
          <div className={`progress-step ${getStatusStep() >= 1 ? 'active' : ''}`}>
            <div className="step-circle">1</div>
            <span>Confirmed</span>
          </div>
          <div className={`progress-line ${getStatusStep() >= 2 ? 'active' : ''}`}></div>
          <div className={`progress-step ${getStatusStep() >= 2 ? 'active' : ''}`}>
            <div className="step-circle">2</div>
            <span>On the way</span>
          </div>
          <div className={`progress-line ${getStatusStep() >= 3 ? 'active' : ''}`}></div>
          <div className={`progress-step ${getStatusStep() >= 3 ? 'active' : ''}`}>
            <div className="step-circle">3</div>
            <span>Delivered</span>
          </div>
        </div>

        {/* Delivery Confirmation OTP */}
        {deliveryOtp && status !== 'delivered' && (
          <div className="delivery-otp-banner">
            <div className="otp-info">
              <span className="otp-badge">🔐 DELIVERY CONFIRMATION OTP</span>
              <p>Share this code with your delivery partner to receive your order</p>
            </div>
            <div className="otp-code-pill">{deliveryOtp}</div>
          </div>
        )}

        {/* Rider Info Card */}
        {riderInfo && (
          <div className="rider-info-card-premium">
            <div className="rider-avatar">
              <img src={riderInfo?.user?.profilePic || "https://api.dicebear.com/7.x/avataaars/svg?seed=Felix"} alt="Rider" />
            </div>
            <div className="rider-details">
              <div className="rider-header">
                <h3>{riderInfo?.user?.name || riderInfo?.name || "Rider Assigned"}</h3>
                <div className="rider-rating">⭐ {riderInfo?.rating?.average || riderInfo?.averageRating || 'New'}</div>
              </div>
              <p>Your delivery partner</p>
            </div>
            <button className="call-rider-btn" onClick={() => {
              const phone = riderInfo?.user?.mobile || riderInfo?.phone;
              if (phone) {
                window.location.href = `tel:${phone}`;
              } else {
                setFormBanner({ type: 'error', text: 'Rider phone number not available.' });
              }
            }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
              </svg>
            </button>
          </div>
        )}

        {/* Simulation Control (Only for Demo) */}
        {status !== 'delivered' && status !== 'out_for_delivery' && (
          <button className="start-delivery-btn" onClick={() => setStatus('out_for_delivery')}>
            Simulate Delivery Start
          </button>
        )}
      </div>
    </div>
  );
};

export default LiveTracking;
